import { ChangeEvent, KeyboardEvent, useEffect, useRef, useState } from "react"
import { Control, Controller, FieldErrors, FieldValues } from "react-hook-form"
import { IoSearch } from "react-icons/io5"
import { LuChevronLeft, LuMoveLeft } from "react-icons/lu"
import AutosizeInput from "react-input-autosize"
import { useLocation, useNavigate } from "react-router-dom"
import Slider, { Settings } from "react-slick"
import { toast } from "react-toastify"

import BackgroundWrapper from "@fuse/sharedComponents/backgroundWrapper"
import BlurWrapper from "@fuse/sharedComponents/blurWrapper"
import SolidBtn from "@fuse/sharedComponents/buttons/SolidBtn"
import Checkbox from "@fuse/sharedComponents/checkbox"
import BlurredLoadingSpinner from "@fuse/sharedComponents/loader/BlurredLoadingSpinner"
import RadioGroup from "@fuse/sharedComponents/radioGroup"
import { showErrorToast } from "@fuse/sharedComponents/themeToast/toastUtils"
import FancyText from "@fuse/sharedComponents/typography/FancyText"
import { Question, QUESTIONNAIRES_TYPE } from "@letshang/constants"
import { get, isEmpty, size } from "lodash-es"
import "slick-carousel/slick/slick-theme.css"
import "slick-carousel/slick/slick.css"

import { useFillQuestionnaire } from "@fuse/pages/eventSignUp/BuyTickets/FillQuestionnaire/useFillQuestionnaire"
import {
  getThePreciseLocation,
  usePredictionAutocomplete,
} from "@fuse/pages/explore/utility/locationFunctions"
import useProfileController from "@fuse/pages/profilePage/useProfileController"
import {
  capitalizeSentence,
  getDateFormatForCountry,
  getInputConfig,
} from "@fuse/pages/signUp/steps/Dob"
import { DobType } from "@fuse/pages/signUp/steps/Dob"

import css from "./style.module.css"

const AutosizeInputText = AutosizeInput as any

const VALID_KEY = {
  YEAR: "year",
  MONTH: "month",
  DAY: "day",
  MESSAGE: "message",
}
const settings: Settings = {
  dots: false,
  infinite: false,
  initialSlide: 0,
  slidesToShow: 1,
  slidesToScroll: 1,
  autoplay: false,
  className: `${css.customSlickSlideChildCss}`,
  arrows: false,
}
const SlickSlider = Slider as any

export const CustomDateController = ({
  onChange,
  value,
  refs,
  user,
  error,
  errorClassname,
}) => {
  const [isValidDate, setIsValidDate] = useState(true)
  const [formattedDate, setFormattedDate] = useState<string | null>(null)
  const [date, setDate] = useState<DobType>({
    day: "",
    month: "",
    year: "",
  })
  const dateFormat = getDateFormatForCountry(user?.country)
  const inputConfig = getInputConfig(dateFormat)

  const getNextInput = (currentKey: string): keyof typeof refs | null => {
    const sortedInputs = Object.entries(inputConfig)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)

    const currentIndex = sortedInputs.indexOf(currentKey)
    return currentIndex < sortedInputs.length - 1
      ? (sortedInputs[currentIndex + 1] as keyof typeof refs)
      : null
  }

  const getPrevInput = (currentKey: string): keyof typeof refs | null => {
    const sortedInputs = Object.entries(inputConfig)
      .sort(([, a], [, b]) => a.order - b.order)
      .map(([key]) => key)

    const currentIndex = sortedInputs.indexOf(currentKey)
    return currentIndex > 0
      ? (sortedInputs[currentIndex - 1] as keyof typeof refs)
      : null
  }
  const handleKeyDown = (
    key: keyof DobType,
    e: KeyboardEvent<HTMLInputElement>,
  ) => {
    const nextInput = getNextInput(key)
    const prevInput = getPrevInput(key)
    const maxLength = inputConfig[key].maxLength

    if (e.currentTarget.value.length >= maxLength && e.key !== "Backspace") {
      nextInput && refs[nextInput].current?.focus()
    }
    if (e.key === "Backspace" && e.currentTarget.value === "") {
      prevInput && refs[prevInput].current?.focus()
    }
  }
  const handleInputChange = (
    key: keyof DobType,
    e: ChangeEvent<HTMLInputElement>,
    onChange: (_: any) => void,
  ) => {
    const value = e.target.value

    setDate((prev) => {
      const newVal = { ...prev, [key]: e.target.value }
      onChange(newVal)
      return newVal
    })
  }

  const VALID_KEY_SET = [
    VALID_KEY.YEAR,
    VALID_KEY.MONTH,
    VALID_KEY.DAY,
    VALID_KEY.MESSAGE,
  ]

  return (
    <div className="flex flex-col items-start w-full justify-center">
      <div
        className={`px-4 bg-white/20 h-12 rounded-md flex justify-center w-full items-center ${isValidDate ? "" : "border"} border-red-400`}
      >
        <div className="flex items-center w-full justify-start">
          {Object.entries(inputConfig)
            .sort(([, a], [, b]) => a.order - b.order)
            .map(([key, config], index, array) => {
              return (
                <div key={key} className="flex items-start flex-shrink-0">
                  <AutosizeInputText
                    inputRef={(input: any) => {
                      refs[key as keyof typeof refs].current = input
                    }}
                    name={index}
                    inputMode="numeric"
                    placeholder={capitalizeSentence(config.placeholder)}
                    value={value?.[key] ?? ""}
                    maxLength={config.maxLength}
                    onChange={(e: any) => {
                      handleInputChange(key as keyof DobType, e, onChange)
                    }}
                    onKeyDown={(e: any) =>
                      handleKeyDown(key as keyof DobType, e)
                    }
                    className="h-full text-[16px] text-black placeholder-gray-500 outline-none text-left w-auto max-w-none flex-shrink-0 placeholder:capitalize"
                    inputStyle={styles}
                  />
                  {index < array.length - 1 && (
                    <span className="text-[16px] text-white opacity-[0.3]">
                      /
                    </span>
                  )}
                </div>
              )
            })}
        </div>
      </div>
      <p
        className={`text-white/35 mt-2 ${isValidDate ? "" : "text-[#f87171]"}`}
      >
        {isValidDate ? formattedDate : ""}
      </p>

      {error &&
        Object.keys(error as object).some((i) => VALID_KEY_SET.includes(i)) && (
          <FancyText
            className={`mt-[1rem] text-md text-textError font-medium text-start w-full ${errorClassname}`}
          >
            {Object.entries(error).map(
              ([key, value]: [string, { message?: string } | string]) => {
                return key === "message" ? (
                  <FancyText key={key}>
                    {typeof value === "string" && value}
                  </FancyText>
                ) : (
                  <FancyText key={key}>
                    {typeof value !== "string" && value?.message}
                  </FancyText>
                )
              },
            )}
          </FancyText>
        )}
    </div>
  )
}

const RenderQuestion = ({
  question,
  index,
  currentIndex,
  errors,
  control,
  form,
}: {
  question: Question
  index: number
  currentIndex: number
  errors: FieldErrors<FieldValues>
  control: Control<FieldValues, any>
  form: any
}) => {
  const questionText = get(question, "question", "")
  const options = get(question, "options", [])
  const inputRefs = useRef<
    Record<number, HTMLTextAreaElement | HTMLInputElement | null>
  >({}) // Use a record to map index to ref
  const questionType = get(question, "questionType", "")

  const { user } = useProfileController()

  const dateFormat = getDateFormatForCountry(user?.country)

  const inputQuestionTypes = [
    QUESTIONNAIRES_TYPE.LONG_TEXT,
    QUESTIONNAIRES_TYPE.TEXT,
    QUESTIONNAIRES_TYPE.NUMBER,
    QUESTIONNAIRES_TYPE.ADDRESS,
    QUESTIONNAIRES_TYPE.PHONE,
    QUESTIONNAIRES_TYPE.DATE,
  ]

  useEffect(() => {
    if (inputRefs.current[currentIndex]) {
      if (question && inputQuestionTypes.includes(question?.questionType)) {
        const inputAreaRef = inputRefs.current[currentIndex]
        if (inputAreaRef.value.length) {
          inputAreaRef?.focus()
          // Focus specific text area
          if (inputAreaRef.type === "textarea") {
            inputAreaRef?.setSelectionRange(
              inputAreaRef.value.length,
              inputAreaRef.value.length,
            ) // Move cursor to the end
          }
        }
      }
    }
  })

  const refs = {
    day: useRef<HTMLInputElement>(null),
    month: useRef<HTMLInputElement>(null),
    year: useRef<HTMLInputElement>(null),
  }
  const locTerm = form[index] || ""

  switch (question.questionType) {
    case QUESTIONNAIRES_TYPE.ADDRESS:
      return AddressInput(
        questionText,
        index,
        control,
        inputRefs,
        errors,
        getThePreciseLocation,
        locTerm,
      )
    case QUESTIONNAIRES_TYPE.DATE:
      return (
        <div className=" w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full  text-left">
              <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
                {`${questionText}:`}
              </FancyText>
            </div>
          </div>

          <div className="flex flex-col items-center h-[32rem] xl:h-[26rem] mt-8 md:mt-14 relative">
            <Controller
              control={control}
              name={`${index}`}
              key={index}
              render={({ field: { onChange, onBlur, value } }) => (
                <CustomDateController
                  user={user}
                  onChange={onChange}
                  value={value}
                  refs={refs}
                  error={errors[index]}
                  errorClassname={"md:text-[24px]"}
                />
              )}
            />
          </div>
        </div>
      )

    case QUESTIONNAIRES_TYPE.NUMBER:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
                {`${questionText}:`}
              </FancyText>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center h-[32rem] xl:h-[26rem] mt-8 md:mt-14 relative">
            <Controller
              control={control}
              name={`${index}`}
              key={index}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className="w-full h-full max-w-lg">
                  <input
                    ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                    type="number"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Enter a number"
                    className="text-[26px] md:text-[26px] bg-transparent focus:outline-none focus:ring-0 placeholder-white/30 text-white w-full"
                  />
                </div>
              )}
            />
          </div>

          {errors[index] && (
            <FancyText className="text-[16px] md:text-[24px] text-textError font-[500] text-start w-full">
              {errors[index]?.message?.toString()}
            </FancyText>
          )}
        </div>
      )
    case QUESTIONNAIRES_TYPE.EMAIL:
      return (
        <div className=" w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full  text-left">
              <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
                {`${questionText}:`}
              </FancyText>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center h-[32rem] xl:h-[26rem] mt-8 md:mt-14 relative">
            <Controller
              control={control}
              name={`${index}`}
              key={index}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className=" w-full h-full max-w-lg">
                  <input
                    ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                    type="email"
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Enter your email here"
                    className="text-[26px] md:text-[26px] bg-transparent focus:outline-none focus:ring-0 placeholder-white/30 text-white"
                    style={{ resize: "none" }}
                  />
                </div>
              )}
            />
          </div>

          {errors[index] && (
            <FancyText className="text-[16px] md:text-[24px] text-textError font-[500] text-start w-full">
              {errors[index]?.message?.toString()}
            </FancyText>
          )}
        </div>
      )
    case QUESTIONNAIRES_TYPE.PHONE:
    case QUESTIONNAIRES_TYPE.TEXT:
    case QUESTIONNAIRES_TYPE.LONG_TEXT:
      return (
        <div className=" w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full  text-left">
              <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
                {`${questionText}:`}
              </FancyText>
            </div>
          </div>

          <div className="flex flex-col items-center justify-center h-[32rem] xl:h-[26rem] mt-8 md:mt-14 relative">
            <Controller
              control={control}
              name={`${index}`}
              key={index}
              render={({ field: { onChange, onBlur, value } }) => (
                <div className=" w-full h-full max-w-lg">
                  <textarea
                    ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                    value={value}
                    onChange={onChange}
                    onBlur={onBlur}
                    placeholder="Write your answer here"
                    style={{ resize: "none" }}
                    className="text-[26px] md:text-[26px] bg-transparent focus:outline-none focus:ring-0 placeholder-white/30 text-white"
                  />
                </div>
              )}
            />
          </div>

          {errors[index] && (
            <FancyText className="text-[16px] md:text-[24px] text-textError font-[500] text-start w-full">
              {errors[index]?.message?.toString()}
            </FancyText>
          )}
        </div>
      )

    case QUESTIONNAIRES_TYPE.SINGLE_SELECT:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-center justify-start">
            <div className="w-full text-left">
              <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
                {`${questionText}:`}
              </FancyText>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-14">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue=""
              render={({ field: { onChange, value } }) => {
                return (
                  <RadioGroup
                    options={options}
                    selectedValue={value}
                    onChange={onChange}
                  />
                )
              }}
            />
            {errors[index] && (
              <FancyText className="text-[16px] md:text-[24px] text-textError font-[500]">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    case QUESTIONNAIRES_TYPE.MULTI_SELECT:
      return (
        <div className="w-full">
          <div className="flex flex-col w-full items-start justify-start">
            <div className="w-full text-left">
              <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
                {`${questionText}:`}
              </FancyText>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 self-center mt-14">
            <Controller
              control={control}
              name={`${index}`}
              defaultValue={[]}
              render={({ field: { onChange, value } }) => {
                return (
                  <Checkbox
                    options={options}
                    selectedValues={value}
                    onChange={onChange}
                  />
                )
              }}
            />
            {errors[index] && (
              <FancyText className="text-[16px] md:text-[24px] text-textError font-[500]">
                {errors[index]?.message?.toString()}
              </FancyText>
            )}
          </div>
        </div>
      )

    default:
      return null
  }
}

const FillQuestionnaire = () => {
  const navigate = useNavigate()
  const [currentIndex, setCurrentIndex] = useState(0)
  const sliderRef = useRef<Slider>(null)
  const { state } = useLocation()

  const {
    eventId,
    isLoading,
    watch,
    handleSubmit,
    errors,
    onSubmit,
    flyerImage,
    control,
  } = useFillQuestionnaire(state.invitedId, "fillQuestionnaire")

  const form = watch()
  const questionnairesSize = size(state?.attendeeQuestioners)

  const buttonTitle =
    currentIndex < state?.attendeeQuestioners?.length - 1
      ? "Next question"
      : "Submit"

  useEffect(() => {
    sliderRef?.current?.slickGoTo(0)
  }, [])

  const handlePress = () => {
    const nextPage = currentIndex + 1
    if (nextPage < questionnairesSize) {
      sliderRef?.current?.slickGoTo(nextPage)
    } else {
      if (Object.keys(errors).length > 0) {
        showErrorToast(
          "Please ensure all questions are completed before submitting!",
        )
      }
      handleSubmit(onSubmit)()
    }
  }

  const handleBackPress = () => {
    if (currentIndex > 0) {
      sliderRef?.current?.slickGoTo(currentIndex - 1)
    } else {
      // navigate(ROUTES.CHOOSE_TICKET.replace(":eventId", eventId));
      navigate(-1)
    }
  }

  const Header: React.FC = () => {
    return (
      <div className="mt-4 ml-4 md:mt-12 md:ml-12 flex items-center space-x-2 z-10">
        <div
          onClick={handleBackPress}
          className="w-12 h-12 md:w-14 md:h-14 lg:w-16 lg:h-16 rounded-full flex justify-center items-center bg-white/10 cursor-pointer z-10"
        >
          <LuChevronLeft className="text-white text-2xl mr-0.5 md:hidden" />
          <LuMoveLeft className="text-white text-md md:text-2xl hidden md:flex" />
        </div>
        <div className="flex w-full items-center justify-center absolute left-[-8px]">
          <p className="text-md md:text-[26px] font-semibold text-white">
            Questions from the host
          </p>
        </div>
      </div>
    )
  }

  return (
    <>
      <BackgroundWrapper bgImage={flyerImage} className="min-h-screen">
        <BlurWrapper
          overlayOpacityDefault="75"
          overlayColorDefault="dark"
          className="min-h-screen"
        >
          {/* <Navbar /> */}
          <Header />
          {eventId && isLoading && <BlurredLoadingSpinner />}

          <div className="relative z-10 flex flex-col items-center justify-center h-full text-center px-6 mt-16">
            <div className="text-left w-full max-w-lg mb-6 ">
              <p className="text-white/30 text-left text-[12px] md:text-sm font-semibold">
                QUESTION {currentIndex + 1} OF {questionnairesSize}
              </p>

              <div className="slider-container">
                {!isEmpty(state?.attendeeQuestioners) && (
                  <SlickSlider
                    ref={sliderRef}
                    {...settings}
                    beforeChange={(currentSlide: number, nextSlide: number) =>
                      setCurrentIndex(nextSlide)
                    }
                  >
                    {state?.attendeeQuestioners?.map((question, index) => (
                      <div key={index} className="w-full">
                        <RenderQuestion
                          question={question}
                          index={index}
                          currentIndex={currentIndex}
                          errors={errors}
                          form={form}
                          control={control}
                        />
                      </div>
                    ))}
                  </SlickSlider>
                )}
              </div>
            </div>

            {!isEmpty(form[currentIndex]) && (
              <div className="flex items-center justify-center self-center">
                <SolidBtn
                  // label={buttonTitle}
                  size="lg"
                  className="px-6 lg:px-8 min-w-80 "
                  onClick={handlePress}
                >
                  {buttonTitle}
                </SolidBtn>
              </div>
            )}
          </div>
        </BlurWrapper>
      </BackgroundWrapper>
    </>
  )
}
const AddressInput = (
  questionText: string,
  index: number,
  control: any,
  inputRefs: any,
  errors: any,
  getThePreciseLocation: any,
  locTerm: string,
) => {
  const { predictionList } = usePredictionAutocomplete({
    locTerm,
    isSessionRequired: true,
  })

  return (
    <div className=" w-full">
      <div className="flex flex-col w-full items-center justify-start">
        <div className="w-full  text-left">
          <FancyText className="text-white font-semibold text-[15px] md:text-[26px]">
            {`${questionText}:`}
          </FancyText>
        </div>
      </div>

      <div className="flex flex-col items-center justify-center h-[32rem] xl:h-[26rem] mt-8 md:mt-14 relative">
        <Controller
          control={control}
          name={`${index}`}
          key={index}
          render={({ field: { onChange, onBlur, value } }) => (
            <div className=" w-full h-full max-w-lg">
              <textarea
                ref={(el) => (inputRefs.current[index] = el)} // Assign ref dynamically based on index
                value={value}
                onChange={(e) => {
                  onChange(e.target.value)
                }}
                onBlur={onBlur}
                placeholder="Write your answer here"
                style={{ resize: "none" }}
                className="text-[26px] md:text-[26px] bg-transparent focus:outline-none focus:ring-0 placeholder-white/30 text-white"
              />

              <button
                onClick={() => {
                  getThePreciseLocation((location) => {
                    onChange(location?.location?.nameOfPlace)
                  })
                }}
                className="flex items-center gap-3 bg-gray-800 hover:bg-gray-700 text-white px-4 py-2 rounded-lg shadow-md w-full max-w-sm transition-all"
              >
                <IoSearch color="#ffffff80" size={20} />
                <div className="flex flex-col text-left">
                  <span className="text-sm font-semibold">
                    Detect the location
                  </span>
                  <span className="text-xs text-gray-400">Using GPS</span>
                </div>
              </button>

              {predictionList.length > 0 && (
                <ul className="top-full left-0 w-full bg-gray-800 text-white rounded-md max-h-40 my-5 overflow-y-auto z-10">
                  {predictionList.map((suggestion, i) => (
                    <li
                      key={i}
                      className="p-2 cursor-pointer hover:bg-gray-700"
                      onClick={() => {
                        onChange(suggestion?.description)
                      }}
                    >
                      {suggestion?.description}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        />
      </div>

      {errors[index] && (
        <FancyText className="text-[16px] md:text-[24px] text-textError font-[500] text-start w-full">
          {errors[index]?.message?.toString()}
        </FancyText>
      )}
    </div>
  )
}

const styles = {
  color: "white",
  backgroundColor: "transparent",
  outline: "none",
  boxShadow: "none",
}

export default FillQuestionnaire
